import { Component, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { filter, map, take } from 'rxjs/operators';
import { RouteService } from 'src/app/core/services/route.service';
import { LoadApplication } from 'src/app/core/state/_actions/application.actions';
import { ApplicationData } from 'src/app/core/state/_models/application.models';
import { getApplicationById } from 'src/app/core/state/_selectors/application.selector';
import { getClientDetails } from 'src/app/core/state/_selectors/clients.selector';
import { getFields } from 'src/app/core/state/_selectors/field.selector';
import { getForm } from 'src/app/core/state/_selectors/form.selector';
import { getImports } from 'src/app/core/state/_selectors/import.selector';
import { getNotification } from 'src/app/core/state/_selectors/notification.selector';
import { getPurges } from 'src/app/core/state/_selectors/purge.selector';
import { getRelation } from 'src/app/core/state/_selectors/relation.selector';
import {
  getCalendarData,
  getGanttData,
  getMapData,
  getReportWFilters,
  getSummary,
} from 'src/app/core/state/_selectors/report.selector';
import { getAllDashboards } from 'src/app/core/state/_selectors/settings/dashboards.selector';
import { getPageDetail } from 'src/app/core/state/_selectors/settings/pages.selector';
import { getRolesDetail } from 'src/app/core/state/_selectors/settings/roles.selector';
import { getNavigation } from 'src/app/core/state/_selectors/table.selector';
import { SubSink } from 'subsink';
import { routeToAppRoute, routeToName } from './breadcrums.constants';

interface Breadcrumb {
  isLink: boolean;
  routeLink: any;
  resolve: Promise<any> | null;
  value: string;
  show?: boolean;
  actionBtn?: {
    state: boolean;
    icon: string;
    tooltip: string;
    routeLink: string;
  };
}

@Component({
  selector: 'app-breadcrums',
  templateUrl: './breadcrums.component.html',
  styleUrls: ['./breadcrums.component.scss'],
})
export class BreadcrumsComponent implements OnInit {
  omitKeys = [
    'details',
    'form',
    'edit',
    'apps',
    'reports',
    'reports/datatable/view',
    'tables',
    'notifications',
    'imports',
    'fields',
    'relations',
    'forms',
    'templates/edit',
    'pages/edit',
    'layout/edit',
    '/dashboard/edit',
    '/superset-dashboard/edit',
    'theme/edit'
  ];

  keys = [
    'details',
    'form',
    'edit',
    'apps',
    'reports',
    'reports/datatable/view',
    'tables',
    'notifications',
    'imports',
    'fields',
    'relations',
    'forms',
    'datatable',
    'summary',
    'chart',
    'calendar',
    'map',
    'gantt',
    'view',
    'web',
    'pdf',
    'word',
    'notification',
    'subscription',
    'webhook',
    'purges',
    'roles',
    'users',
    'user-interface',
    'settings',
    'table-metrics',
    'view-settings',
    'audit-log',
    'copy-app',
    'workflows',
    'tracking',
    'templates',
    'pages',
    'layout',
    'dashboard',
    'superset-dashboard',
    'theme',
  ];

  fixed = [
    'Reports (Datatable)',
    'Reports (Datatable)',
    'Reports (Chart)',
    'Reports (Chart)',
    'Reports (Map)',
    'Reports (Map)',
    'Reports (Gantt)',
    'Reports (Gantt)',
    'Reports (Summary)',
    'Reports (Summary)',
    'Reports (Calendar)',
    'Reports (Calendar)',
    'Forms (Web)',
    'Forms (Mobile)',
    'Forms (PDF)',
    'Forms (Word)',
    'Notifications',
    'Subscriptions',
    'Webhooks',
    'Forms (Add)',
    'Fields (Summary)',
    'Dashboards',
    'View Page',
    'Edit Page',
    'Create Page',
    'Summary field',
    'purges',
    'roles',
    'users',
    'user-interface',
    'templates',
    'pages',
    'layout',
    'theme',
  ];

  public omitValues = ['form', 'summaryFieldId','reportId'];
  applicationData: ApplicationData = <ApplicationData>{};
  activeIndex: number = 1;
  breadcrumbs: Array<Breadcrumb> = [];
  promiseMap: { [type: string]: Promise<string> } = {};
  subsink = new SubSink();

  constructor(private store: Store, private route: RouteService) {}

  ngOnInit(): void {
    this.loadSubscribers();
    this.makeBreadcrumbs();
  }

  /**
   * Subscribers
   */
  public loadSubscribers = () => {
    this.subsink.add(
    this.route.change().subscribe(() => {
      this.makeBreadcrumbs();
    }),
    this.store.pipe(
      select(getApplicationById),
      filter((data) => !!data[this.route.getApplicationKey()]),
      map((data) => data[this.route.getApplicationKey()])
    ).subscribe(()=> {
      this.makeBreadcrumbs()
    }))
  };

  public breadcrumbsObject = (splitURL: Array<string>) => {
    let key = [],
      json: any = {};
    for (let item of splitURL) {
      if (this.keys.includes(item)) key.push(item);
      else {
        json[key.join('/')] = item;
        key = [];
      }
    }
    return {
      ...json,
      ...Object.entries(key).reduce(
        (a, v) => ({
          ...a,
          [v[1]]: -1,
        }),
        {}
      ),
    };
  };

  /**
   * Make breadcrumbs from current route
   */
  public makeBreadcrumbs = () => {
    let splitURL = this.route.currentRoute().split('/').splice(1);
    let tempArray: any = [];
    splitURL.forEach((ele: any) => {
      tempArray.push(ele?.includes('?') ? ele.split('?')[0] : ele);
    });
    splitURL = tempArray;
    const rawObj = this.breadcrumbsObject(splitURL);
    if (this.route.currentRoute().includes('file-view')) {
      delete rawObj[''];
      delete rawObj['file-view'];
    }
    this.breadcrumbs = [];
    for (let prop in rawObj) {
      if (!this.omitKeys.includes(prop))
        this.breadcrumbs.push({
          isLink: this.isLink('', prop),
          value: routeToName[prop] ? routeToName[prop] : prop,
          show: this.resolve(prop, rawObj[prop]) ? true : false,
          resolve: this.resolve(prop, rawObj[prop]),
          routeLink: this.routeLink(splitURL, prop),
        });
      if (rawObj[prop] !== -1 && !this.omitValues.includes(prop))
        this.breadcrumbs.push({
          isLink: this.isLink(prop, rawObj[prop]),
          value: routeToName[rawObj[prop]]
            ? routeToName[rawObj[prop]]
            : rawObj[prop] == ''
            ? 'Default'
            : rawObj[prop],
          show: this.resolve(prop, rawObj[prop]) ? true : false,
          resolve: this.resolve(prop, rawObj[prop]),
          routeLink: this.routeLink(splitURL, rawObj[prop]),
          actionBtn: {
            state:
              prop.includes('reports') &&
              !prop.includes('datatable') &&
              prop.includes('view') &&
              rawObj[prop] !== '',
            icon: 'fe fe-edit ml-1',
            tooltip: 'Edit',
            routeLink: (this.routeLink(splitURL, rawObj[prop]) || [])
              .join('/')
              .replace('view/', ''),
          },
        });
    }
  };

  /**
   * @param item breadcrumb
   * @returns true if link
   */
  public isLink = (prop: string, item: string) => {
    if (prop === 'apps') return true;
    if (prop.includes('reports') || prop.includes('relations')) return true;
    return this.keys.includes(item);
  };

  /**
   * @param item breadcrumb
   * @returns route url
   */
  public routeLink = (splitURL: Array<string>, item: string) => {
    if (
      ['settings/user-interface/dashboard/view', 'summaryFieldId'].indexOf(
        item
      ) > -1
    )
      return undefined;
    const restRoute = routeToAppRoute[item] || [];
    let index = splitURL.indexOf(item);
    if (index === -1) index = 3;
    return [...splitURL.slice(0, index + 1), ...restRoute];
  };

  /**
   * Resolve *id* (appId, tableId) to corresponding name (from store)
   * @returns
   */
  public resolve = (prop: string, item: string) => {

    switch (prop) {
      case 'apps':
        return this.store
          .pipe(
            select(getApplicationById),
            filter((data) => !!data[this.route.getApplicationKey()]),
            map((data) => 'Home'),
            take(1)
          )
          .toPromise();
      case 'tables':
        return this.store
          .pipe(
            select(getNavigation),
            filter((data) => !!data[this.route.getApplicationKey()]),
            map(
              (data) =>
                data[this.route.getApplicationKey()].find(
                  (t) => t?.LevelIds?.TableId == item
                )?.Title
            ),
            take(1)
          )
          .toPromise();
      case 'field/summary':
        return this.store
          .pipe(
            select(getFields),
            filter((data) => !!data[this.route.getTableKey()]),
            map(
              (data) =>
                data[this.route.getTableKey()].find((t) => t.Key == item)?.Name
            ),
            take(1)
          )
          .toPromise();
      case 'reports/summary/view':
      case 'reports/chart/view':
      case 'reports/datatable/view':
      case 'reports/summary':
      case 'reports/chart':
        return this.store
          .pipe(
            select(getSummary),
            filter(
              (data) =>
                !!data[this.route.getTableKey()] &&
                !!data[this.route.getTableKey()][this.route.paramMap.reportId]
            ),
            map(
              (data) =>
                data[this.route.getTableKey()][this.route.paramMap.reportId]
                  .Name
            ),
            take(1)
          )
          .toPromise();
      case 'relations':
        return this.store
          .pipe(
            select(getRelation),
            filter(
              (data) =>
                !!data[this.route.getTableKey()] &&
                !!data[this.route.getTableKey()][this.route.paramMap.relationId]
            ),
            map((data) => {
              const { ApplicationTableRelation, ApplicationTableRelation2 } =
                data[this.route.getTableKey()][this.route.paramMap.relationId];
              return `${ApplicationTableRelation.Name} to ${ApplicationTableRelation2.Name}`;
            }),
            take(1)
          )
          .toPromise();
      case 'forms/web':
      case 'forms/mobile':
      case 'forms/pdf':
      case 'forms/word':
        return this.store
          .pipe(
            select(getForm),
            filter(
              (forms) =>
                !!forms[this.route.getTableKey()] &&
                !!forms[this.route.getTableKey()][this.route.paramMap.formId]
            ),
            map(
              (form) =>
                form[this.route.getTableKey()][this.route.paramMap.formId].Name
            ),
            take(1)
          )
          .toPromise();
      case 'roles':
        return this.store
          .pipe(
            select(getRolesDetail),
            filter(
              (roles) =>
                !!roles[this.route.getApplicationKey()] &&
                !!roles[this.route.getApplicationKey()][
                  this.route.paramMap.roleId
                ]
            ),
            map(
              (role) =>
                role[this.route.getApplicationKey()][this.route.paramMap.roleId]
                  .Name
            ),
            take(1)
          )
          .toPromise();
      case 'page':
        return this.store
          .pipe(
            select(getPageDetail),
            filter(
              (page) =>
                !!page[this.route.getApplicationKey()] &&
                !!page[this.route.getApplicationKey()][
                  this.route.paramMap.pageId
                ]
            ),
            map(
              (page) =>
                page[this.route.getApplicationKey()][this.route.paramMap.pageId]
                  .Title
            ),
            take(1)
          )
          .toPromise();
      case 'dashboard':
      case 'settings/user-interface/dashboard/view':
        return this.store
          .pipe(
            select(getAllDashboards),
            filter((data) => !!data[this.route.getApplicationKey()]),
            map((data) => {
              const dashboard = data[this.route.getApplicationKey()].find(
                (d: any) => d.Key === this.route.paramMap.dashboardId
              );
              return dashboard?.Name;
            }),
            take(1)
          )
          .toPromise();
      case 'imports':
        return this.store
          .pipe(
            select(getImports),
            filter((data) => !!data[this.route.paramMap.importId]),
            map(
              (data) =>
                data[this.route.paramMap.importId].find(
                  (d: any) => d.Key === this.route.paramMap.importId
                )?.Name
            ),
            take(1)
          )
          .toPromise();
      case 'purges':
        return this.store
          .pipe(
            select(getPurges),
            filter((data) => !!data[this.route.paramMap.purgeId]),
            map(
              (data) =>
                data[this.route.paramMap.purgeId].find(
                  (d: any) => d.Key === this.route.paramMap.purgeId
                )?.Name
            ),
            take(1)
          )
          .toPromise();
      case 'details':
      case 'edit':
        if (this.route.paramMap.dashboardId != null)
          return Promise.resolve(`Record: #${this.route.paramMap.dashboardId}`);
        return Promise.resolve(`Record: #${this.route.paramMap.id}`);
      case 'notifications/notification':
      case 'notifications/subscription':
      case 'notifications/webhook':
        return this.store
          .pipe(
            select(getNotification),
            filter(
              (data) =>
                !!data[this.route.getTableKey()] &&
                !!data[this.route.getTableKey()][this.route.paramMap.id]
            ),
            map(
              (data) =>
                data[this.route.getTableKey()][this.route.paramMap.id].Subject
            ),
            take(1)
          )
          .toPromise();
      case 'reports/datatable':
      case 'reports/map/view':
      case 'reports/calendar/view':
      case 'reports/gantt/view':
        if (location.href.includes('/gantt/view'))
          return this.store
            .pipe(
              select(getGanttData),
              filter(
                (reports) =>
                  !!reports[this.route.getTableKey()] &&
                  !!reports[this.route.getTableKey()][
                    this.route.paramMap.reportId
                  ]
              ),
              map(
                (reports) =>
                  reports[this.route.getTableKey()][
                    this.route.paramMap.reportId
                  ].ReportData.Name
              ),
              take(1)
            )
            .toPromise();
        if (location.href.includes('/map/view'))
          return this.store
            .pipe(
              select(getMapData),
              filter(
                (reports) =>
                  !!reports[this.route.getTableKey()] &&
                  !!reports[this.route.getTableKey()][
                    this.route.paramMap.reportId
                  ]
              ),
              map(
                (reports) =>
                  reports[this.route.getTableKey()][
                    this.route.paramMap.reportId
                  ].Name
              ),
              take(1)
            )
            .toPromise();
        if (location.href.includes('/calendar/view'))
          return this.store
            .pipe(
              select(getCalendarData),
              filter(
                (reports) =>
                  !!reports[this.route.getTableKey()] &&
                  !!reports[this.route.getTableKey()][
                    this.route.paramMap.reportId
                  ]
              ),
              map(
                (reports) =>
                  reports[this.route.getTableKey()][
                    this.route.paramMap.reportId
                  ].ReportData.Name
              ),
              take(1)
            )
            .toPromise();
        return this.store
          .pipe(
            select(getReportWFilters),
            filter((reports) => !!reports[this.route.paramMap.reportId]),
            map((reports) => reports[this.route.paramMap.reportId].Name),
            take(1)
          )
          .toPromise();
      case 'clients':
        return this.store.pipe(select(getClientDetails), take(1)).toPromise();
      // default:
      // return Promise.resolve(`${type[0].toUpperCase()}${type.slice(1)}`);
    }
    if (
      this.route.paramMap.clientId != '' &&
      this.route.paramMap.clientId != undefined &&
      this.route.paramMap.clientId != null &&
      this.route.paramMap.clientId == prop
    ) {
      return Promise.resolve(prop);
    }
    return null;
  };

  parentOfId = (splitURL: Array<string>, item: string) => {
    let index = splitURL.findIndex((i) => i == item) - 1;
    // in case of any view
    while (splitURL[index] === 'view') index--;
    // in case of forms
    while (splitURL[index] === 'web' || splitURL[index] === 'mobile') index--;
    return splitURL[index];
  };

  ngOnDestroy(){
    this.subsink.unsubscribe();
  }
}
